//#region Libraries
import { useEffect, useState } from "react";
//#endregion
//#region Styles
import {
	Column,
	Fields,
	Part,
	SubtitleSection,
	TitleSection,
} from "components/styles";
//#endregion
//#region Components
import HandlerCheckbox from "components/CustomCheckbox/CustomCheckbox";
import CertificationSignatureSlot from "./components/CertificationSignatureSlot/CertificationSignatureSlot";
import {
	AlertCard,
	AlertStack,
	CertificationBlock,
	CertificationHeader,
	CompactInfoRow,
	CompactInfoText,
	ConfigurationShell,
	ContextText,
	HeaderMain,
	HeaderCopy,
	HeaderTopRow,
	MutedPanel,
	QuantityButton,
	QuantityGroup,
	SignatureGrid,
	StatusChip,
	ToggleGroup,
} from "./styles";
//#endregion
//#region Services
import { requestGetAvailableCertificationSignaturesByCampus } from "services/events/certification";
//#endregion
//#region Utils
import useEventFormContext from "views/Eventos/components/FormCreateEvent/ManualLoadingForm/utils/context/useEventFormContext";
import {
	hasDuplicateSignatureTypes,
	getCertificationTeacherReference,
	invalidateInstitutionalAutoCertificateSignatures,
	hasInvalidInstitutionalSignatureValidity,
	isCertificationTeacherReferenceResolved,
	reconcileAutoCertificateWithAvailableSignatures,
	reconcileAutoCertificateWithCampus,
	syncCertificationSignatureTypeCatalog,
} from "views/Eventos/utils/certification";
//#endregion

const signatureCountOptions = [1, 2, 3];

const Part4Certification = ({
	form,
	errors,
	errorTexts,
	organizerCampus,
	eventEndDate,
	teacher,
	certificationActions,
}) => {
	const { data } = useEventFormContext();
	const { certificationSignatureTypes = [] } = data;
	const { autoCertificate } = form;
	const [availableInstitutionalSignatures, setAvailableInstitutionalSignatures] =
		useState([]);
	const [hasLoadedInstitutionalCatalog, setHasLoadedInstitutionalCatalog] =
		useState(false);
	const [hasInstitutionalCatalogError, setHasInstitutionalCatalogError] =
		useState(false);
	const teacherReference = getCertificationTeacherReference(teacher);
	const hasOrganizerCampus = Boolean(organizerCampus?.id);
	const hasEventEndDate = Boolean(eventEndDate);
	const teacherResolved =
		isCertificationTeacherReferenceResolved(teacherReference);
	const hasLiveDuplicateTypes = hasDuplicateSignatureTypes(
		autoCertificate.signatures
	);
	const hasLiveValidityError = hasInvalidInstitutionalSignatureValidity(
		autoCertificate.signatures
	);
	const hasTeacherSlotSelected = autoCertificate.signatures.some(
		signature => signature?.signatureTypeCode === "DOCENTE_PONENTE"
	);
	const alerts = [
		Boolean(errors?.backendCampus) && {
			key: "campus",
			variant: "error",
			priority: 10,
			text:
				errorTexts.autoCertificateCampus ||
				"Completa el campus organizador antes de configurar la generación de constancias.",
		},
		Boolean(errors?.signatureCount) && {
			key: "count",
			variant: "error",
			priority: 20,
			text:
				errorTexts.autoCertificateSignatureCount ||
				"Selecciona entre 1 y 3 firmas para continuar.",
		},
		Boolean(errors?.signatures) && {
			key: "signatures",
			variant: "error",
			priority: 30,
			text:
				errorTexts.autoCertificateSignatures ||
				"Revisa la configuración de firmas antes de continuar.",
		},
		(Boolean(errors?.duplicateTypes) || hasLiveDuplicateTypes) && {
			key: "duplicate-types",
			variant: "error",
			priority: 40,
			text: "No repitas el mismo tipo de firma dentro de la misma configuración de constancias.",
		},
		Boolean(errors?.invalidValidity) && {
			key: "invalid-validity",
			variant: "error",
			priority: 50,
			text:
				errorTexts.autoCertificateValidity ||
				"Hay firmas sin una resolución válida para el campus y la fecha del evento.",
		},
		Boolean(errors?.backendTeacherSpeaker) && {
			key: "teacher-speaker",
			variant: "error",
			priority: 60,
			text:
				errorTexts.autoCertificateTeacherSpeaker ||
				"El docente o ponente no tiene una firma válida para la constancia del evento.",
		},
		hasInstitutionalCatalogError && {
			key: "catalog",
			variant: "warning",
			priority: 80,
			text:
				"No pudimos validar las firmas en este momento. Puedes guardar y dejar la validación final al backend.",
		},
		hasLiveValidityError && {
			key: "live-validity",
			variant: "error",
			priority: 70,
			text:
				"Hay firmas pendientes, inválidas o ambiguas. Revisa los slots marcados antes de guardar.",
		},
		(!teacherResolved && hasTeacherSlotSelected) && {
			key: "teacher-required",
			variant: "error",
			priority: 65,
			text:
				"Si usas DOCENTE / PONENTE, completa ese dato en 'Organizado por'.",
		},
	]
		.filter(Boolean)
		.sort((left, right) => left.priority - right.priority);
	const primaryAlert = alerts[0] || null;

	const changeToggle = () => {
		certificationActions.setEnabled(!autoCertificate.enabled);
	};

	useEffect(() => {
		syncCertificationSignatureTypeCatalog(certificationSignatureTypes);
	}, [certificationSignatureTypes]);

	useEffect(() => {
		if (!autoCertificate.enabled) return;
		const reconciled = reconcileAutoCertificateWithCampus(
			autoCertificate,
			organizerCampus?.id || 0
		);
		if (JSON.stringify(reconciled) === JSON.stringify(autoCertificate)) return;
		certificationActions.replace(reconciled);
	}, [
		autoCertificate.enabled,
		autoCertificate,
		organizerCampus?.id,
		certificationActions,
	]);

	useEffect(() => {
		if (!autoCertificate.enabled) return;
		const invalidated = invalidateInstitutionalAutoCertificateSignatures(
			autoCertificate,
			{
				campusId: organizerCampus?.id || 0,
				eventEndDate,
				teacherReference,
			}
		);
		if (JSON.stringify(invalidated) === JSON.stringify(autoCertificate)) return;
		certificationActions.replace(invalidated);
	}, [
		autoCertificate.enabled,
		teacherReference?.fullName,
		teacherReference?.utpCode,
		organizerCampus?.id,
		eventEndDate,
		certificationActions,
	]);

	useEffect(() => {
		if (!autoCertificate.enabled || !organizerCampus?.id || !eventEndDate) {
			setAvailableInstitutionalSignatures([]);
			setHasLoadedInstitutionalCatalog(false);
			setHasInstitutionalCatalogError(false);
			return;
		}
		let isMounted = true;
		const fillAvailableSignatures = async () => {
			try {
				const result = await requestGetAvailableCertificationSignaturesByCampus({
					campusId: organizerCampus.id,
					eventEndDate,
				});
				if (!isMounted) return;
				if (result === null) {
					setAvailableInstitutionalSignatures([]);
					setHasLoadedInstitutionalCatalog(false);
					setHasInstitutionalCatalogError(true);
					return;
				}
				setAvailableInstitutionalSignatures(result);
				setHasLoadedInstitutionalCatalog(true);
				setHasInstitutionalCatalogError(false);
			} catch (error) {
				if (!isMounted) return;
				setAvailableInstitutionalSignatures([]);
				setHasLoadedInstitutionalCatalog(false);
				setHasInstitutionalCatalogError(true);
			}
		};
		fillAvailableSignatures();
		return () => {
			isMounted = false;
		};
	}, [autoCertificate.enabled, organizerCampus?.id, eventEndDate]);

	useEffect(() => {
		if (
			!autoCertificate.enabled ||
			!organizerCampus?.id ||
			!eventEndDate ||
			!hasLoadedInstitutionalCatalog
		)
			return;
		const reconciled = reconcileAutoCertificateWithAvailableSignatures(
			autoCertificate,
			availableInstitutionalSignatures,
			{
				campusId: organizerCampus.id,
				eventEndDate,
				teacherReference,
			}
		);
		if (JSON.stringify(reconciled) === JSON.stringify(autoCertificate)) return;
		certificationActions.replace(reconciled);
	}, [
		autoCertificate,
		autoCertificate.enabled,
		availableInstitutionalSignatures,
		hasLoadedInstitutionalCatalog,
		organizerCampus?.id,
		eventEndDate,
		teacherReference?.fullName,
		teacherReference?.utpCode,
		certificationActions,
	]);

	return (
		<Part>
			<CertificationHeader>
				<HeaderMain>
						<HeaderTopRow>
							<HeaderCopy>
								<TitleSection margin="0" width="unset">
									Generación automática de constancias
								</TitleSection>
							</HeaderCopy>
							<ToggleGroup>
								<StatusChip $active={autoCertificate.enabled}>
									{autoCertificate.enabled ? "Activada" : "Desactivada"}
								</StatusChip>
							<HandlerCheckbox
								variant="switch"
								onChange={changeToggle}
								checkboxProps={[
									{
										value: "auto-certificate",
										label: "",
										checked: autoCertificate.enabled,
									},
								]}
							/>
						</ToggleGroup>
					</HeaderTopRow>
					{autoCertificate.enabled ? (
						<ConfigurationShell>
							<CompactInfoRow>
								<CompactInfoText>Firmas</CompactInfoText>
								<QuantityGroup>
									{signatureCountOptions.map(value => (
										<QuantityButton
											key={value}
											type="button"
											$active={autoCertificate.signatureCount === value}
											onClick={() =>
												certificationActions.setSignatureCount(value)
											}
										>
											{value}
										</QuantityButton>
									))}
								</QuantityGroup>
							</CompactInfoRow>
							<ContextText>
								Campus:{" "}
								<strong>{organizerCampus?.name || "No definido"}</strong>
							</ContextText>
						</ConfigurationShell>
					) : null}
				</HeaderMain>
			</CertificationHeader>
			<Fields>
				{!autoCertificate.enabled ? null : !hasOrganizerCampus ? (
					<MutedPanel>
						<h4>Falta el campus organizador</h4>
						<p>Defínelo para validar las firmas.</p>
					</MutedPanel>
				) : !hasEventEndDate ? (
					<MutedPanel>
						<h4>Falta la fecha fin del evento</h4>
						<p>La vigencia se valida con esa fecha.</p>
					</MutedPanel>
				) : (
					<CertificationBlock>
						{primaryAlert && (
							<AlertStack>
								<AlertCard $variant={primaryAlert.variant}>
									<p>
										{primaryAlert.key === "signatures"
											? "Hay firmas por completar."
											: primaryAlert.text}
									</p>
								</AlertCard>
							</AlertStack>
						)}
						{autoCertificate.signatureCount === 0 ? (
							<MutedPanel>
								<h4>Selecciona la cantidad de firmas</h4>
								<p>Elige 1, 2 o 3 para continuar.</p>
							</MutedPanel>
						) : (
							<Column gap="10px">
								<SubtitleSection fontSize="15px">
									Firmas seleccionadas
								</SubtitleSection>
								<SignatureGrid $count={autoCertificate.signatureCount}>
								{autoCertificate.signatures.map((signature, idx) => (
									<CertificationSignatureSlot
										key={idx}
										slotIndex={idx}
										signature={signature}
										allSignatures={autoCertificate.signatures}
										error={errors?.signatureItems?.[idx]}
										signatureTypeOptions={
											certificationSignatureTypes
										}
										organizerCampusName={organizerCampus?.name || ""}
										teacherReference={teacherReference}
										onChange={value =>
											certificationActions.setSignature(idx, value)
										}
									/>
								))}
								</SignatureGrid>
							</Column>
						)}
					</CertificationBlock>
				)}
			</Fields>
		</Part>
	);
};

export default Part4Certification;
